import { PayloadAction, createSlice } from "@reduxjs/toolkit";

export type LoopMode = "single" | "sequence" | "shuffle" | "smart";

interface PlaybackState {
  backgroundPlaybackEnabled: boolean;
  screenCoverVisible: boolean;
  loopMode: LoopMode;
  webAudioUnlocked: boolean;
}

export const WEB_AUDIO_UNLOCK_STORAGE_KEY = "kirkwat-web-audio-unlocked";
export const LOOP_MODE_STORAGE_KEY = "kirkwat-playback-loop-mode";

function storedWebAudioUnlock() {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return (
      window.localStorage.getItem(WEB_AUDIO_UNLOCK_STORAGE_KEY) === "1" ||
      window.sessionStorage.getItem(WEB_AUDIO_UNLOCK_STORAGE_KEY) === "1"
    );
  } catch {
    return false;
  }
}

const loopOrder: LoopMode[] = ["single", "sequence", "shuffle", "smart"];

function storedLoopMode(): LoopMode {
  if (typeof window === "undefined") {
    return "single";
  }
  try {
    const value = window.localStorage.getItem(LOOP_MODE_STORAGE_KEY);
    return loopOrder.includes(value as LoopMode)
      ? (value as LoopMode)
      : "single";
  } catch {
    return "single";
  }
}

const initialState: PlaybackState = {
  backgroundPlaybackEnabled: false,
  screenCoverVisible: false,
  loopMode: storedLoopMode(),
  webAudioUnlocked: storedWebAudioUnlock(),
};

const playbackSlice = createSlice({
  name: "playback",
  initialState,
  reducers: {
    toggleBackgroundPlayback: (state) => {
      state.backgroundPlaybackEnabled = !state.backgroundPlaybackEnabled;
    },
    enableBackgroundPlayback: (state) => {
      state.backgroundPlaybackEnabled = true;
    },
    disableBackgroundPlayback: (state) => {
      state.backgroundPlaybackEnabled = false;
      state.screenCoverVisible = false;
    },
    showScreenCover: (state) => {
      state.screenCoverVisible = true;
    },
    hideScreenCover: (state) => {
      state.screenCoverVisible = false;
    },
    cycleLoopMode: (state) => {
      const index = loopOrder.indexOf(state.loopMode);
      state.loopMode = loopOrder[(index + 1) % loopOrder.length];
    },
    setLoopMode: (state, action: PayloadAction<LoopMode>) => {
      state.loopMode = action.payload;
    },
    unlockWebAudio: (state) => {
      state.webAudioUnlocked = true;
    },
    lockWebAudio: (state) => {
      state.webAudioUnlocked = false;
    },
  },
});

export const {
  cycleLoopMode,
  disableBackgroundPlayback,
  enableBackgroundPlayback,
  hideScreenCover,
  setLoopMode,
  showScreenCover,
  toggleBackgroundPlayback,
  lockWebAudio,
  unlockWebAudio,
} = playbackSlice.actions;
export default playbackSlice.reducer;
